import { Enrollee } from "pmt-types/src/models/Enrollee.ts";
import { Renewal } from "pmt-types/src/models/Renewal.ts";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import DeleteConfirmationModal from "../../components/app/deleteConfirmationModal.tsx";
import ProjectDetailsCard from "../../components/app/projectDetailsCard.tsx";
import ProjectEnrolleeList from "../../components/app/projectEnrolleeList.tsx";
import ProjectRenewalList from "../../components/app/projectRenewalList.tsx";
import ErrorLabel from "../../components/form/errorLabel.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import PageHeader from "../../components/pageHeader.tsx";
import TabbedHeader from "../../components/tabbedHeader.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationDeleteEnrollee } from "../../hooks/mutations/useMutationDeleteEnrollee.ts";
import { useMutationDeleteProject } from "../../hooks/mutations/useMutationDeleteProject.ts";
import { useMutationDeleteRenewal } from "../../hooks/mutations/useMutationDeleteRenewal.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetEnrolleesForProjectId } from "../../hooks/queries/useQueryGetEnrolleesForProjectId.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetRenewalsForProjectId } from "../../hooks/queries/useQueryGetRenewalsForProjectId.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";
import useQueryParams from "../../hooks/useQueryParams.ts";
import { Maybe } from "../../utils/types/maybe.ts";

export default function ProjectDetails() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { sourceId, projectId } = useParams();
  const queryParams = useQueryParams();
  const defaultTab = queryParams.get("tab");

  const [selectedTab, setSelectedTab] = useState<"enrollees" | "renewals">(
    defaultTab === "renewals" ? "renewals" : "enrollees",
  );

  const { data: source, isFetching: isFetchingSource } =
    useQueryGetSourceById(sourceId);
  const { data: project, isFetching: isFetchingProject } =
    useQueryGetProjectById(sourceId, projectId);
  const { data: enrollees, isFetching: isFetchingProjectEnrollees } =
    useQueryGetEnrolleesForProjectId(sourceId, projectId);
  const { data: renewals, isFetching: isFetchingProjectRenewals } =
    useQueryGetRenewalsForProjectId(sourceId, projectId);

  const {
    mutate: deleteProject,
    isLoading: isProjectDeleting,
    error: projectDeleteError,
  } = useMutationDeleteProject();

  const {
    mutate: deleteEnrollee,
    isLoading: isEnrolleeDeleting,
    error: enrolleeDeleteError,
  } = useMutationDeleteEnrollee();

  const {
    mutate: deleteRenewal,
    isLoading: isRenewalDeleting,
    error: renewalDeleteError,
  } = useMutationDeleteRenewal();

  const [deletingEnrollee, setDeletingEnrollee] =
    useState<Maybe<Enrollee>>(null);
  const [deletingRenewal, setDeletingRenewal] = useState<Maybe<Renewal>>(null);

  const [isEnrolleeDeleteModalOpen, setIsEnrolleeDeleteModalOpen] =
    useState(false);
  const [isProjectDeleteModalOpen, setIsProjectDeleteModalOpen] =
    useState(false);
  const [isRenewalDeleteModalOpen, setIsRenewalDeleteModalOpen] =
    useState(false);

  if (sourceId == null || projectId == null) {
    return <NotFoundPage />;
  }

  if (
    isFetchingProject ||
    isFetchingSource ||
    isFetchingProjectEnrollees ||
    isFetchingProjectRenewals
  ) {
    return <LoadingSpinner />;
  }

  if (source == null || project == null || enrollees == null) {
    return <NotFoundPage />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-12 h-full overflow-y-auto">
        <PageHeader title={"Project Details"} />
        {projectDeleteError != null && (
          <ErrorLabel message={projectDeleteError?.toString()} />
        )}
        <div className="mt-12 flex flex-col gap-2">
          <ProjectDetailsCard
            source={source}
            project={project}
            onEdit={() =>
              navigate(`/sources/${sourceId}/projects/${projectId}`)
            }
            onDelete={() => {
              setIsProjectDeleteModalOpen(true);
            }}
          />
          <TabbedHeader
            tabs={[
              {
                name: "Enrollees",
                isSelected: selectedTab === "enrollees",
                onSelected: () => {
                  setSelectedTab("enrollees");
                },
              },
              {
                name: "Renewals",
                isSelected: selectedTab === "renewals",
                onSelected: () => {
                  setSelectedTab("renewals");
                },
              },
            ]}
            actions={
              selectedTab === "renewals"
                ? [
                    {
                      label: "Renew Project",
                      onClick: () => {
                        navigate(
                          `/sources/${sourceId}/projects/${projectId}/renewals/new`,
                        );
                      },
                    },
                  ]
                : [
                    {
                      label: "Enroll Contractor",
                      onClick: () => {
                        navigate(
                          `/sources/${sourceId}/projects/${projectId}/enrollees/new?return=project`,
                        );
                      },
                    },
                  ]
            }
          />
          {selectedTab === "enrollees" && (
            <ProjectEnrolleeList
              showHeader={false}
              enrollees={enrollees ?? []}
              onViewEnrollee={(enrollee) =>
                navigate(
                  `/sources/${sourceId}/projects/${projectId}/enrollees/${enrollee.id}/details`,
                )
              }
              onEditEnrollee={(enrollee) =>
                navigate(
                  `/sources/${sourceId}/projects/${projectId}/enrollees/${enrollee.id}?return=project`,
                )
              }
              onDeleteEnrollee={(enrollee) => {
                setDeletingEnrollee(enrollee);
                setIsEnrolleeDeleteModalOpen(true);
              }}
            />
          )}
          {selectedTab === "renewals" && (
            <ProjectRenewalList
              showHeader={false}
              renewals={renewals}
              onDeleteRenewal={(renewal) => {
                setDeletingRenewal(renewal);
                setIsRenewalDeleteModalOpen(true);
              }}
            />
          )}
          <DeleteConfirmationModal
            entityName="enrollee"
            isOpen={isEnrolleeDeleteModalOpen}
            isLoading={isEnrolleeDeleting}
            onConfirm={() => {
              if (deletingEnrollee == null) {
                setIsEnrolleeDeleteModalOpen(false);
                return;
              }

              deleteEnrollee(
                {
                  sourceId: source.id,
                  projectId: project.id,
                  enrolleeId: deletingEnrollee.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([
                      QueryKeys.GetProjectEnrollees,
                      project.id,
                    ]);
                    setDeletingEnrollee(null);
                    setIsEnrolleeDeleteModalOpen(false);
                  },
                  onError: (error) =>
                    console.error("Error deleting enrollee", error),
                },
              );
            }}
            onClose={() => setIsEnrolleeDeleteModalOpen(false)}
          />
          <DeleteConfirmationModal
            entityName="renewal"
            isOpen={isRenewalDeleteModalOpen}
            isLoading={isRenewalDeleting}
            onConfirm={() => {
              if (deletingRenewal == null) {
                setIsRenewalDeleteModalOpen(false);
                return;
              }

              deleteRenewal(
                {
                  sourceId: source.id,
                  projectId: project.id,
                  renewalId: deletingRenewal.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([
                      QueryKeys.GetProjectRenewals,
                      project.id,
                    ]);
                    setDeletingRenewal(null);
                    setIsRenewalDeleteModalOpen(false);
                  },
                  onError: (error) =>
                    console.error("Error deleting renewal", error),
                },
              );
            }}
            onClose={() => setIsRenewalDeleteModalOpen(false)}
          />
          <DeleteConfirmationModal
            entityName="project"
            isOpen={isProjectDeleteModalOpen}
            isLoading={isProjectDeleting}
            onConfirm={() => {
              if (source == null || project == null) {
                setIsProjectDeleteModalOpen(false);
                return;
              }

              deleteProject(
                {
                  sourceId: source.id,
                  projectId: project.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([
                      QueryKeys.GetSourceProjects,
                      source.id,
                    ]);
                    setIsProjectDeleteModalOpen(false);
                    navigate(`/sources/${sourceId}/details`);
                  },
                  onError: (error) =>
                    console.error("Error deleting project", error),
                },
              );
            }}
            onClose={() => setIsProjectDeleteModalOpen(false)}
          />
          {enrolleeDeleteError != null && (
            <ErrorLabel message={enrolleeDeleteError?.toString()} />
          )}
          {renewalDeleteError != null && (
            <ErrorLabel message={renewalDeleteError?.toString()} />
          )}
        </div>
      </div>
    </div>
  );
}
